import { useMemo, useRef } from 'react';
import { useStreamLiveGuestRidesSubscription } from '../queries/streamLiveGuestRides.subscription.generated';
import { Cursor_Ordering } from '@gql/base-types';
import { useRooftop, useUser } from '@store';
import {
  ACTIVE_STATUSES,
  UPCOMING_STATUSES,
  HISTORY_STATUSES,
  DELETED_STATUSES,
} from '../constants/guestRideConstants';

// The StreamLiveGuestRides operation lives in ../queries/streamLiveGuestRides.subscription.gql
// and is codegen'd into its neighbour streamLiveGuestRides.subscription.generated.ts
// (useStreamLiveGuestRidesSubscription).
//
// Hasura STREAMING subscription (cursor on updated_at): a plain live query
// re-sends the ENTIRE matching result set on every change (N live rides → N row
// writes per tick, re-rendering every cache observer incl. an open dialog);
// streaming pushes only the rows whose updated_at advanced, so one ride ticking
// writes one row. Mirrors the dashboard's MovesSubscriptionProvider (moves_stream).
// We intentionally do NOT filter by status — filtering to live statuses would
// mean a ride going terminal never streams its final write, so the page could
// never learn it left the live set.

const TERMINAL_STATUSES = new Set<string>([...HISTORY_STATUSES, ...DELETED_STATUSES]);
const LIVE_STATUSES = new Set<string>([...UPCOMING_STATUSES, ...ACTIVE_STATUSES]);

// ─── Hook ───────────────────────────────────────────────────────────────────

// Streams every guestride change for the current customer since a short
// lookback. Apollo merges each delta row into the normalized cache by id, so a
// live ride's field changes (status flip, driver assigned, price, ETA) update
// the rendered rows in place with NO refetch.
//
// `onSetChange` is fired only when the *membership* of the live set changes — a
// new live ride appears, or a live ride goes terminal — because those are the
// two cases the paginated list + stat buckets can't reconcile from an in-place
// cache write (a new row may be off the current page; a terminal row must move
// buckets). We track the live-id set here rather than reading it off the stream
// payload, since a streaming delta is per-row, not the whole set.
//
// CAVEAT for the feature owner: the cursor advances on guestrides.updated_at.
// If a child ridehail row changes WITHOUT bumping the parent guestride's
// updated_at (backend-trigger dependent), that change won't stream until the
// parent is next touched. Verify the ride lifecycle bumps guestrides.updated_at
// on ridehail transitions; if not, a companion ridehails_stream (cursored on the
// ridehail's own updated_at) is the follow-up.
export function useGuestRideSubscription(onSetChange?: () => void) {
  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;
  const hasCustomer = !!customerId;

  // Start the stream from a short lookback so a reconnect (or a change that
  // landed just before mount) is replayed rather than missed. 15 min mirrors the
  // dashboard provider.
  const initialCursor = useMemo(
    () => [
      {
        initial_value: { updated_at: new Date(Date.now() - 15 * 60 * 1000).toISOString() },
        ordering: Cursor_Ordering.Asc,
      },
    ],
    [],
  );

  const variables = useMemo(
    () => ({
      cursor: initialCursor,
      where: { customer_id: { _eq: customerId } },
    }),
    [initialCursor, customerId],
  );

  const onSetChangeRef = useRef(onSetChange);
  onSetChangeRef.current = onSetChange;

  // The set of ride ids we currently believe are live. Seeded lazily from the
  // stream; membership transitions (add/remove) are what trigger a refetch.
  const liveIdsRef = useRef<Set<number>>(new Set());

  useStreamLiveGuestRidesSubscription({
    variables,
    skip: !hasCustomer,
    onData: ({ data: { data } }) => {
      const rows: Array<{ id: number; status: string }> | undefined = data?.guestrides_stream;
      if (!rows?.length) return;

      let membershipChanged = false;
      for (const row of rows) {
        const isLive = LIVE_STATUSES.has(row.status);
        const wasTracked = liveIdsRef.current.has(row.id);
        if (isLive && !wasTracked) {
          liveIdsRef.current.add(row.id); // new live ride entered the set
          membershipChanged = true;
        } else if (!isLive && wasTracked && TERMINAL_STATUSES.has(row.status)) {
          liveIdsRef.current.delete(row.id); // a tracked ride went terminal
          membershipChanged = true;
        }
        // otherwise: an in-place update to an already-tracked live ride —
        // Apollo has already merged it into the cache, no refetch needed.
      }

      if (membershipChanged) onSetChangeRef.current?.();
    },
  });
}
